import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Sparkles, Megaphone, Heart, Users, Handshake, DollarSign, Award, Plane, MessageCircle, Mic2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
const expectationIcons = [ShieldCheck, Sparkles, Megaphone, Heart, Users, Handshake];
const platformIcons = [DollarSign, Award, Plane, MessageCircle, Mic2];
export function Expectations() {
  const {
    t
  } = useLanguage();
  const items = t('expectationsPage.items') as {
    title: string;
    desc: string;
  }[];
  const platformItems = t('expectationsPage.platformItems') as {
    title: string;
    desc: string;
  }[];
  useEffect(() => {
    setPageMeta({
      title: "What's Expected from Dancers",
      description: "What's expected from dancers on Dance for Fans — content, promotion, fan engagement, collaboration, and partnership."
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#170a1f',
    color: '#f5e6f5'
  }} data-id="element-281">
      {/* Magenta ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-282">
        <motion.div className="absolute top-[-20%] left-[5%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(217, 70, 239, 0.15)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-283" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(103, 232, 249, 0.12)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-284" />
      </div>

      <div className="relative z-10" data-id="element-285">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(245, 230, 245, 0.1)'
      }} data-id="element-286">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-287">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#f5e6f5'
          }} data-id="element-288">
              <ArrowLeft className="w-4 h-4" data-id="element-289" />
              {t('common.backToMain')}
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: 'rgba(245, 230, 245, 0.5)'
          }} data-id="element-290">
              {t('expectationsPage.headerTag')}
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 pt-24 pb-12" data-id="element-291">
          <div className="max-w-3xl mx-auto text-center" data-id="element-292">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-xs uppercase tracking-[0.4em] mb-6" style={{
            color: '#d946ef'
          }} data-id="element-293">
              {t('expectationsPage.heroTag')}
            </motion.p>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif text-5xl md:text-7xl leading-[1.05] mb-8" style={{
            color: '#fdf4fd'
          }} data-id="element-294">
              {t('expectationsPage.heroTitle')}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="font-serif italic text-xl md:text-2xl" style={{
            color: 'rgba(245, 230, 245, 0.75)'
          }} data-id="element-295">
              {t('expectationsPage.heroSubtitle')}
            </motion.p>
          </div>
        </section>

        {/* Expectations list */}
        <section className="px-6 py-16" data-id="element-296">
          <div className="max-w-3xl mx-auto space-y-6" data-id="element-297">
            {items.map((item, i) => {
            const Icon = expectationIcons[i % expectationIcons.length];
            return <motion.article key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="rounded-3xl p-6 md:p-8 border flex gap-5 items-start" style={{
              backgroundColor: 'rgba(40, 15, 50, 0.5)',
              borderColor: 'rgba(217, 70, 239, 0.2)'
            }} data-id="element-298">
                  <div className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center" style={{
                backgroundColor: 'rgba(217, 70, 239, 0.15)'
              }} data-id="element-299">
                    <Icon className="w-6 h-6" style={{
                  color: '#d946ef'
                }} data-id="element-300" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-301">
                    <div className="flex items-baseline gap-3 mb-2" data-id="element-302">
                      <span className="font-serif text-2xl" style={{
                    color: 'rgba(245, 230, 245, 0.4)'
                  }} data-id="element-303">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h2 className="font-serif text-xl md:text-2xl leading-snug" style={{
                    color: '#fdf4fd'
                  }} data-id="element-304">
                        {item.title}
                      </h2>
                    </div>
                    <p className="text-base md:text-lg leading-relaxed" style={{
                  color: 'rgba(245, 230, 245, 0.75)'
                }} data-id="element-305">
                      {item.desc}
                    </p>
                  </div>
                </motion.article>;
          })}
          </div>
        </section>

        {/* Reciprocal: What Dancers Should Expect from Dance for Fans */}
        <section className="px-6 pt-4 pb-16" data-id="element-306">
          <div className="max-w-3xl mx-auto" data-id="element-307">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-10" data-id="element-308">
              <div className="h-px w-24 mx-auto mb-10" style={{
              backgroundColor: 'rgba(103, 232, 249, 0.4)'
            }} data-id="element-309" />
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#67e8f9'
            }} data-id="element-310">
                {t('expectationsPage.platformTag')}
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight" style={{
              color: '#fdf4fd'
            }} data-id="element-311">
                {t('expectationsPage.platformTitle')}
              </h2>
            </motion.div>

            <div className="space-y-6" data-id="element-312">
              {platformItems.map((item, i) => {
              const Icon = platformIcons[i % platformIcons.length];
              return <motion.article key={i} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: i * 0.05
              }} className="rounded-3xl p-6 md:p-8 border flex gap-5 items-start" style={{
                backgroundColor: 'rgba(15, 35, 50, 0.5)',
                borderColor: 'rgba(103, 232, 249, 0.2)'
              }} data-id="element-313">
                    <div className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(103, 232, 249, 0.15)'
                }} data-id="element-314">
                      <Icon className="w-6 h-6" style={{
                    color: '#67e8f9'
                  }} data-id="element-315" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-316">
                      <div className="flex items-baseline gap-3 mb-2" data-id="element-317">
                        <span className="font-serif text-2xl" style={{
                      color: 'rgba(245, 230, 245, 0.4)'
                    }} data-id="element-318">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <h3 className="font-serif text-xl md:text-2xl leading-snug" style={{
                      color: '#fdf4fd'
                    }} data-id="element-319">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-base md:text-lg leading-relaxed" style={{
                    color: 'rgba(245, 230, 245, 0.75)'
                  }} data-id="element-320">
                        {item.desc}
                      </p>
                    </div>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="px-6 py-20" data-id="element-321">
          <div className="max-w-2xl mx-auto text-center" data-id="element-322">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-serif italic text-xl md:text-2xl" style={{
            color: 'rgba(245, 230, 245, 0.85)'
          }} data-id="element-323">
              {t('expectationsPage.closing')}
            </motion.p>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(245, 230, 245, 0.1)'
      }} data-id="element-324">
          <div className="max-w-5xl mx-auto flex items-center justify-between text-xs" data-id="element-325">
            <span style={{
            color: 'rgba(245, 230, 245, 0.4)'
          }} data-id="element-326">
              © {new Date().getFullYear()} danceforfans.com
            </span>
            <span className="uppercase tracking-[0.3em]" style={{
            color: 'rgba(245, 230, 245, 0.4)'
          }} data-id="element-327">
              {t('expectationsPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}